import type { ReactElement } from 'react';
import {
  Code2,
  FileCode,
  FileText,
  Hammer,
  Network,
  Palette,
  Radio,
  Wrench,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { PluginFeatures } from '@kirin/api-client/contracts/plugins';
import {
  hasAnyEntityFeatures,
  hasAnyFileFeatures,
} from './hasAnyPluginFeatures';

interface PluginFeaturesPanelProps {
  features: PluginFeatures;
}

export function PluginFeaturesPanel({
  features,
}: PluginFeaturesPanelProps): ReactElement {
  const hasEntities = hasAnyEntityFeatures(features);
  const hasFiles = hasAnyFileFeatures(features);

  return (
    <div className="space-y-6 pl-8 py-4">
      {hasEntities && (
        <FeatureGroup heading="Entities">
          <CommandsSection items={features.commands} />
          <AgentsSection items={features.agents} />
          <SkillsSection items={features.skills} />
          <McpServersSection items={features.mcpServers} />
          <HooksSection items={features.hooks} />
          <MonitorsSection items={features.monitors} />
          <ThemesSection items={features.themes} />
        </FeatureGroup>
      )}

      {hasFiles && (
        <FeatureGroup heading="Bundled files">
          <ExecutablesSection items={features.files?.executables} />
          <InstructionsSection items={features.files?.instructions} />
          {features.files?.truncated && (
            <p className="text-xs text-muted-foreground italic">
              Scan capped — additional files exist on disk.
            </p>
          )}
        </FeatureGroup>
      )}
    </div>
  );
}

function FeatureGroup({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <section className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {heading}
      </h4>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function FeatureSubsection({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}): ReactElement {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 text-xs font-medium">
        <Icon className="h-3.5 w-3.5" />
        <span>{title}</span>
      </div>
      <div className="pl-5 space-y-1">{children}</div>
    </div>
  );
}

// ---------- Sections (each renders nothing if its list is empty) ----------

function CommandsSection({
  items,
}: {
  items?: PluginFeatures['commands'];
}): ReactElement | null {
  if (!items?.length) return null;
  return (
    <FeatureSubsection title={`Commands (${items.length})`} icon={Code2}>
      {items.map(c => (
        <div key={c.name} className="text-xs">
          <span className="font-mono">{c.name}</span>
          {c.description && (
            <span className="text-muted-foreground"> — {c.description}</span>
          )}
        </div>
      ))}
    </FeatureSubsection>
  );
}

function AgentsSection({
  items,
}: {
  items?: PluginFeatures['agents'];
}): ReactElement | null {
  if (!items?.length) return null;
  return (
    <FeatureSubsection title={`Agents (${items.length})`} icon={Wrench}>
      {items.map(a => (
        <div
          key={a.name}
          className="text-xs flex flex-wrap items-baseline gap-1.5"
        >
          <span className="font-mono">{a.name}</span>
          {a.model && (
            <Badge variant="outline" className="text-[10px] py-0">
              {a.model}
            </Badge>
          )}
          {a.description && (
            <span className="text-muted-foreground">— {a.description}</span>
          )}
        </div>
      ))}
    </FeatureSubsection>
  );
}

function SkillsSection({
  items,
}: {
  items?: PluginFeatures['skills'];
}): ReactElement | null {
  if (!items?.length) return null;
  return (
    <FeatureSubsection title={`Skills (${items.length})`} icon={Hammer}>
      {items.map(s => (
        <div key={s.name} className="text-xs">
          <span className="font-mono">{s.name}</span>
          {s.description && (
            <span className="text-muted-foreground"> — {s.description}</span>
          )}
        </div>
      ))}
    </FeatureSubsection>
  );
}

function McpServersSection({
  items,
}: {
  items?: PluginFeatures['mcpServers'];
}): ReactElement | null {
  if (!items?.length) return null;
  return (
    <FeatureSubsection title={`MCP servers (${items.length})`} icon={Network}>
      {items.map(m => (
        <div
          key={m.name}
          className="text-xs flex flex-wrap items-baseline gap-1.5"
        >
          <span className="font-mono">{m.name}</span>
          <Badge variant="outline" className="text-[10px] py-0">
            {m.transport}
          </Badge>
          {m.commandRef && (
            <span className="font-mono text-muted-foreground">
              → {m.commandRef}
            </span>
          )}
        </div>
      ))}
    </FeatureSubsection>
  );
}

function HooksSection({
  items,
}: {
  items?: PluginFeatures['hooks'];
}): ReactElement | null {
  if (!items?.length) return null;
  // Group by event
  const byEvent = new Map<string, typeof items>();
  for (const h of items) {
    const list = byEvent.get(h.event) ?? [];
    list.push(h);
    byEvent.set(h.event, list);
  }
  return (
    <FeatureSubsection title={`Hooks (${items.length})`} icon={Radio}>
      {Array.from(byEvent.entries()).map(([event, hooks]) => (
        <div key={event} className="space-y-0.5">
          <div className="text-xs font-medium">{event}</div>
          {hooks.map((h, i) => (
            <div
              key={`${event}:${h.matcher ?? ''}:${i}`}
              className="text-xs pl-3"
            >
              <span className="font-mono">{h.matcher || '*'}</span>
              <span className="text-muted-foreground">
                {' → '}
                {(h.handlerTypes ?? []).join(', ') || 'unknown'} ({h.count}×)
              </span>
              {h.commandRefs && h.commandRefs.length > 0 && (
                <div className="pl-3 text-muted-foreground font-mono">
                  {h.commandRefs.join(', ')}
                </div>
              )}
            </div>
          ))}
        </div>
      ))}
    </FeatureSubsection>
  );
}

function MonitorsSection({
  items,
}: {
  items?: PluginFeatures['monitors'];
}): ReactElement | null {
  if (!items?.length) return null;
  return (
    <FeatureSubsection title={`Monitors (${items.length})`} icon={Radio}>
      {items.map(m => (
        <div key={m.name} className="text-xs">
          <span className="font-mono">{m.name}</span>
          {m.description && (
            <span className="text-muted-foreground"> — {m.description}</span>
          )}
          {m.commandRef && (
            <span className="font-mono text-muted-foreground">
              {' '}
              → {m.commandRef}
            </span>
          )}
        </div>
      ))}
    </FeatureSubsection>
  );
}

function ThemesSection({
  items,
}: {
  items?: PluginFeatures['themes'];
}): ReactElement | null {
  if (!items?.length) return null;
  return (
    <FeatureSubsection title={`Themes (${items.length})`} icon={Palette}>
      {items.map(t => (
        <div key={t.name} className="text-xs">
          <span>{t.name}</span>
          {t.path && (
            <span className="font-mono text-muted-foreground"> ({t.path})</span>
          )}
        </div>
      ))}
    </FeatureSubsection>
  );
}

function ExecutablesSection({
  items,
}: {
  items?: NonNullable<PluginFeatures['files']>['executables'];
}): ReactElement | null {
  if (!items?.length) return null;
  return (
    <FeatureSubsection title={`Executables (${items.length})`} icon={FileCode}>
      {items.map(e => (
        <div
          key={e.path}
          className="text-xs flex flex-wrap items-center gap-1.5"
        >
          <span className="font-mono">{e.path}</span>
          <Badge variant="outline" className="text-[10px] py-0">
            {e.kind}
          </Badge>
          {e.executableBit && (
            <Badge variant="secondary" className="text-[10px] py-0">
              +x
            </Badge>
          )}
          {e.referencedBy && e.referencedBy.length > 0 ? (
            <span className="text-muted-foreground">
              used by{' '}
              {e.referencedBy.map((id, idx) => (
                <span key={id} className="font-mono">
                  {idx > 0 ? ', ' : ''}
                  {id}
                </span>
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground italic">unreferenced</span>
          )}
        </div>
      ))}
    </FeatureSubsection>
  );
}

function InstructionsSection({
  items,
}: {
  items?: NonNullable<PluginFeatures['files']>['instructions'];
}): ReactElement | null {
  if (!items?.length) return null;
  return (
    <FeatureSubsection title={`Instructions (${items.length})`} icon={FileText}>
      {items.map(ins => (
        <div key={ins.path} className="text-xs">
          <span className="font-mono">{ins.path}</span>
          {ins.title && (
            <span className="text-muted-foreground"> — {ins.title}</span>
          )}
          {ins.referencedBy && ins.referencedBy.length > 0 && (
            <span className="text-muted-foreground">
              {' '}
              · used by {ins.referencedBy.join(', ')}
            </span>
          )}
        </div>
      ))}
    </FeatureSubsection>
  );
}
